import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ClientSelector from '../components/ClientSelector';
import BrandingSettings from '../components/BrandingSettings';
import {
  User, Settings, LogOut, Users, FileText, Target, Calendar,
  TrendingUp, Mail, Plus, Search, ArrowRight, Edit2, X, Copy, Check, Sun, Moon, Trash2
} from 'lucide-react';
import rmmbrLogo from '../assets/rmmbr.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function CoachCRM() {
  const { user, logout, token, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientStats, setClientStats] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('clients'); // 'clients', 'users', or 'settings'
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [invitingClient, setInvitingClient] = useState(null);
  const [invitationLink, setInvitationLink] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [newClientFormData, setNewClientFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    status: 'active',
    preferred_contact_method: '',
    occupation: '',
    company: '',
    timezone: '',
    notes: '',
    referral_source: '',
    medical_conditions: '',
    goals_summary: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    status: 'active',
    preferred_contact_method: '',
    occupation: '',
    company: '',
    timezone: '',
    notes: '',
    referral_source: '',
    medical_conditions: '',
    goals_summary: ''
  });
  const profileMenuRef = useRef(null);

  // Set up axios interceptor to add token to all requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const handleRoleUpdate = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await axios.put(`${API_URL}/users/${userId}/role`, { role: newRole });
      // Update local state
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(`Failed to update role: ${error.response?.data?.error || error.message}`);
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleClientRoleUpdate = async (client, newRole, e) => {
    e.stopPropagation(); // Prevent navigation to client detail

    if (!client.user_id) {
      toast('This client does not have a user account yet. Send them an invitation first.');
      return;
    }

    setUpdatingRole(client.user_id);
    try {
      await axios.put(`${API_URL}/users/${client.user_id}/role`, { role: newRole });
      // Update local state - update both clients and users lists
      setClients(clients.map(c =>
        c.id === client.id ? { ...c, role: newRole } : c
      ));
      setUsers(users.map(u =>
        u.id === client.user_id ? { ...u, role: newRole } : u
      ));
      // Optionally show success message
      // toast(`Role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating client role:', error);
      toast.error(`Failed to update role: ${error.response?.data?.error || error.message}`);
    } finally {
      setUpdatingRole(null);
    }
  };

  const fetchClientStats = useCallback(async () => {
    const stats = {};
    for (const client of clients) {
      try {
        const response = await axios.get(`${API_URL}/clients/${client.id}`);
        const data = response.data;
        stats[client.id] = {
          sessionCount: data.sessionNotes?.length || 0,
          objectiveCount: data.objectives?.length || 0,
          activeObjectives: data.objectives?.filter(obj => obj.status === 'active').length || 0,
          lastSessionDate: data.sessionNotes?.length > 0
            ? (data.sessionNotes[0].session_date || data.sessionNotes[0].created_at)
            : null
        };
      } catch (error) {
        console.error(`Error fetching stats for client ${client.id}:`, error);
      }
    }
    setClientStats(stats);
  }, [clients]);

  // useEffect hooks
  useEffect(() => {
    // Wait for auth to finish loading before fetching clients
    if (!authLoading && user && token) {
      fetchClients();
      fetchUsers();
    }
  }, [authLoading, user, token, fetchClients, fetchUsers]);

  useEffect(() => {
    if (clients.length > 0 && user) {
      fetchClientStats();
    } else {
    }
  }, [clients, user, fetchClientStats]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClientCreate = async (e) => {
    e.preventDefault();
    if (!newClientFormData.name.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/clients`, newClientFormData);
      const newClient = response.data;
      setClients([newClient, ...clients]);

      // Reset form data
      setNewClientFormData({
        name: '', email: '', phone: '', date_of_birth: '', address_street: '',
        address_city: '', address_state: '', address_zip: '', address_country: '',
        emergency_contact_name: '', emergency_contact_phone: '', status: 'active',
        preferred_contact_method: '', occupation: '', company: '', timezone: '',
        notes: '', referral_source: '', medical_conditions: '', goals_summary: ''
      });
      setShowNewClientModal(false);

      // Automatically send invitation for new client if email exists
      if (newClient.email) {
        try {
          const invitationResponse = await axios.post(`${API_URL}/clients/${newClient.id}/send-invitation`);
          const signupUrl = `${window.location.origin}/client/signup?code=${invitationResponse.data.invitationCode}`;
          setInvitationLink(signupUrl);
          setShowInvitationModal(true);
        } catch (inviteError) {
          console.error('Error sending invitation:', inviteError);
          // Don't show error - user can send invitation manually later
        }
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast(`Error creating client: ${error.message}`);
    }
  };

  const handleCancelNewClient = () => {
    setShowNewClientModal(false);
    setNewClientFormData({
      name: '', email: '', phone: '', date_of_birth: '', address_street: '',
      address_city: '', address_state: '', address_zip: '', address_country: '',
      emergency_contact_name: '', emergency_contact_phone: '', status: 'active',
      preferred_contact_method: '', occupation: '', company: '', timezone: '',
      notes: '', referral_source: '', medical_conditions: '', goals_summary: ''
    });
  };

  const handleNewClientFormChange = (field, value) => {
    setNewClientFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (client, e) => {
    e.stopPropagation(); // Prevent navigation to client detail
    setEditingClient(client);
    setEditFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      date_of_birth: client.date_of_birth || '',
      address_street: client.address_street || '',
      address_city: client.address_city || '',
      address_state: client.address_state || '',
      address_zip: client.address_zip || '',
      address_country: client.address_country || '',
      emergency_contact_name: client.emergency_contact_name || '',
      emergency_contact_phone: client.emergency_contact_phone || '',
      status: client.status || 'active',
      preferred_contact_method: client.preferred_contact_method || '',
      occupation: client.occupation || '',
      company: client.company || '',
      timezone: client.timezone || '',
      notes: client.notes || '',
      referral_source: client.referral_source || '',
      medical_conditions: client.medical_conditions || '',
      goals_summary: client.goals_summary || ''
    });
  };

  const handleClientUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) return;

    try {
      const response = await axios.put(`${API_URL}/clients/${editingClient.id}`, editFormData);
      
      // Update the client in the list
      setClients(clients.map(c => c.id === editingClient.id ? response.data : c));
      
      // Refresh stats if needed
      if (clientStats[editingClient.id]) {
        fetchClientStats();
      }
      
      setEditingClient(null);
      setEditFormData({
        name: '', email: '', phone: '', date_of_birth: '', address_street: '',
        address_city: '', address_state: '', address_zip: '', address_country: '',
        emergency_contact_name: '', emergency_contact_phone: '', status: 'active',
        preferred_contact_method: '', occupation: '', company: '', timezone: '',
        notes: '', referral_source: '', medical_conditions: '', goals_summary: ''
      });
    } catch (error) {
      console.error('Error updating client:', error);
      toast(`Error updating client: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
    setEditFormData({
      name: '', email: '', phone: '', date_of_birth: '', address_street: '',
      address_city: '', address_state: '', address_zip: '', address_country: '',
      emergency_contact_name: '', emergency_contact_phone: '', status: 'active',
      preferred_contact_method: '', occupation: '', company: '', timezone: '',
      notes: '', referral_source: '', medical_conditions: '', goals_summary: ''
    });
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendInvitation = async (client, e) => {
    e.stopPropagation(); // Prevent navigation to client detail

    // Check if client already has an account
    if (client.user_id) {
      toast.error('This client already has an account. No invitation needed.');
      return;
    }

    // Generate the invitation code
    try {
      const response = await axios.post(`${API_URL}/clients/${client.id}/send-invitation`);
      const signupUrl = `${window.location.origin}/client/signup?code=${response.data.invitationCode}`;
      setInvitationLink(signupUrl);
      setInvitingClient(client);
      setShowInvitationModal(true);
    } catch (error) {
      console.error('Error generating invitation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to generate invitation. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleSendInvitationEmail = async () => {
    if (!invitingClient || !invitationLink) return;

    try {
      // The backend will send the email when we call the send-invitation endpoint
      // Since we already called it to generate the code, just show a success message
      toast.success(`Invitation email sent to ${invitingClient.email || invitingClient.name}`);
      setShowInvitationModal(false);
      setInvitationLink(null);
      setInvitingClient(null);
    } catch (error) {
      console.error('Error sending invitation email:', error);
      toast.error('Failed to send email. You can still copy the link.');
    }
  };

  const handleDeleteClient = async (client, e) => {
    e.stopPropagation(); // Prevent navigation to client detail
    
    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete ${client.name}? This action cannot be undone and will delete all associated data (session notes, objectives, readings, etc.).`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      // Ensure we have a token
      if (!token) {
        toast('You must be logged in to delete a client. Please log in again.');
        return;
      }

      await axios.delete(`${API_URL}/clients/${client.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Remove client from local state
      setClients(clients.filter(c => c.id !== client.id));
      // Remove stats for deleted client
      setClientStats(prev => {
        const newStats = { ...prev };
        delete newStats[client.id];
        return newStats;
      });
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(`Failed to delete client: ${error.response?.data?.error || error.message}`);
    }
  };

  const copyToClipboard = () => {
    if (invitationLink) {
      navigator.clipboard.writeText(invitationLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No sessions yet';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: '#1a1816' }}>
      {/* Top Navigation Bar */}
      <nav className="bg-[#1a1816] border-b border-[#3a352f] flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <img src={rmmbrLogo} alt="rmmbr" className="h-5 w-auto" />
            </div>

            {/* Theme Toggle and Profile Menu */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-[#2a2520] transition-all duration-200 text-gray-400 hover:text-[#C59D52]"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-[#2a2520] transition-all duration-200 border-2 border-[#C59D52] focus:outline-none"
                >
                  <div className="w-8 h-8 rounded-full bg-[#C59D52]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#C59D52]" />
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#2a2520] border border-[#3a352f] rounded-lg shadow-xl py-2 z-50 animate-in fade-in duration-200">
                    <div className="px-4 py-3 border-b border-[#3a352f]">
                      <p className="text-sm font-semibold text-white">{user?.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        // TODO: Add profile settings functionality
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-[#3a352f] hover:text-white transition-colors flex items-center gap-3"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-[#3a352f] hover:text-red-300 transition-colors flex items-center gap-3"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {activeTab === 'settings' ? 'Settings' : 'Client Management'}
                </h1>
                <p className="text-gray-400">
                  {activeTab === 'settings'
                    ? 'Customize your coaching brand appearance'
                    : 'Manage all your coaching clients and track their progress'
                  }
                </p>
              </div>
              {activeTab === 'clients' && (
                <button
                  onClick={() => setShowNewClientModal(true)}
                  className="flex items-center gap-2 bg-[#D4A574] hover:bg-[#C59D52] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  <span>New Client</span>
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-[#3a352f]">
              <button
                onClick={() => setActiveTab('clients')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'clients'
                    ? 'text-[#D4A574] border-b-2 border-[#D4A574]'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Clients
              </button>
              <button
                onClick={() => {
                  setActiveTab('users');
                  fetchUsers(); // Refresh users when switching to users tab
                }}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'text-[#D4A574] border-b-2 border-[#D4A574]'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                All Users
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'settings'
                    ? 'text-[#D4A574] border-b-2 border-[#D4A574]'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Settings
              </button>
            </div>

            {/* Search Bar - Only show for clients and users tabs */}
            {activeTab !== 'settings' && (
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={activeTab === 'clients' ? "Search clients by name or email..." : "Search users by name or email..."}
                  className="w-full bg-[#2a2520] text-white placeholder-gray-500 px-12 py-3 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                />
              </div>
            )}
          </div>

          {/* Users List */}
          {activeTab === 'users' && (
            <div className="mb-8">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3a352f] border-t-[#C59D52]"></div>
                </div>
              ) : users.filter(u => 
                u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
              ).length === 0 ? (
                <div className="text-center py-20">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-600 opacity-50" />
                  <p className="text-xl font-semibold text-gray-400 mb-2">
                    {searchQuery ? 'No users found' : 'No users yet'}
                  </p>
                </div>
              ) : (
                <div className="bg-[#2a2520] border border-[#3a352f] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#1a1816] border-b border-[#3a352f]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Role</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#3a352f]">
                      {users
                        .filter(u => 
                          u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
                        )
                        .map((user) => (
                          <tr key={user.id} className="hover:bg-[#1a1816] transition-colors">
                            <td className="px-6 py-4 text-white font-medium">{user.name}</td>
                            <td className="px-6 py-4 text-gray-400">{user.email}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                user.role === 'coach' 
                                  ? 'bg-[#C59D52]/20 text-[#D4A574]' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                user.email_confirmed 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {user.email_confirmed ? 'Verified' : 'Unverified'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={user.role}
                                onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
                                disabled={updatingRole === user.id}
                                className="bg-[#1a1816] border border-[#3a352f] text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <option value="client">Client</option>
                                <option value="coach">Coach</option>
                              </select>
                              {updatingRole === user.id && (
                                <span className="ml-2 text-xs text-gray-400">Updating...</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Clients Grid */}
          {activeTab === 'clients' && (
            <>
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3a352f] border-t-[#C59D52]"></div>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-20">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-600 opacity-50" />
                  <p className="text-xl font-semibold text-gray-400 mb-2">
                    {searchQuery ? 'No clients found' : 'No clients yet'}
                  </p>
                  <p className="text-gray-500 mb-6">
                    {searchQuery ? 'Try a different search term' : 'Create your first client to get started'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setShowNewClientModal(true)}
                      className="inline-flex items-center gap-2 bg-[#D4A574] hover:bg-[#C59D52] text-white px-6 py-3 rounded-lg font-semibold transition-all"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Create First Client</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredClients.map((client) => {
                    const stats = clientStats[client.id] || {};
                    return (
                      <div
                        key={client.id}
                        className="bg-[#2a2520] border border-[#3a352f] rounded-lg p-6 hover:border-[#C59D52]/50 transition-all duration-200 group relative"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div 
                            onClick={() => navigate(`/coach/client/${client.id}`)}
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                          >
                            <div className="w-12 h-12 rounded-full bg-[#C59D52]/20 flex items-center justify-center">
                              <User className="w-6 h-6 text-[#C59D52]" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-white group-hover:text-[#C59D52] transition-colors">
                                {client.name}
                              </h3>
                              {client.email && (
                                <div className="flex items-center gap-1.5 text-sm text-gray-400 mt-0.5">
                                  <Mail className="w-3 h-3" />
                                  <span>{client.email}</span>
                                </div>
                              )}
                              {client.user_id && (
                                <div className="flex items-center gap-2 mt-2">
                                  <select
                                    value={client.role || 'client'}
                                    onChange={(e) => handleClientRoleUpdate(client, e.target.value, e)}
                                    disabled={updatingRole === client.user_id}
                                    onClick={(e) => e.stopPropagation()}
                                    className="bg-[#1a1816] border border-[#3a352f] text-white rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <option value="client">Client</option>
                                    <option value="coach">Coach</option>
                                  </select>
                                  {updatingRole === client.user_id && (
                                    <div className="w-3 h-3 border-2 border-[#C59D52] border-t-transparent rounded-full animate-spin"></div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!client.user_id && (
                              <button
                                onClick={(e) => handleSendInvitation(client, e)}
                                disabled={invitingClient?.id === client.id}
                                className="p-2 rounded-lg hover:bg-[#3a352f] text-gray-400 hover:text-[#C59D52] transition-all disabled:opacity-50"
                                title="Send invitation"
                              >
                                {invitingClient?.id === client.id ? (
                                  <div className="w-4 h-4 border-2 border-[#C59D52] border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={(e) => handleEditClick(client, e)}
                              className="p-2 rounded-lg hover:bg-[#3a352f] text-gray-400 hover:text-[#C59D52] transition-all"
                              title="Edit client"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClient(client, e)}
                              className="p-2 rounded-lg hover:bg-red-900/20 text-gray-400 hover:text-red-400 transition-all"
                              title="Delete client"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <ArrowRight 
                              onClick={() => navigate(`/coach/client/${client.id}`)}
                              className="w-5 h-5 text-gray-500 group-hover:text-[#C59D52] transition-colors opacity-0 group-hover:opacity-100 cursor-pointer" 
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-[#1a1816] rounded-lg p-3 border border-[#3a352f]">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="w-4 h-4 text-[#C59D52]" />
                              <span className="text-xs text-gray-400">Sessions</span>
                            </div>
                            <p className="text-xl font-bold text-white">{stats.sessionCount || 0}</p>
                          </div>
                          <div className="bg-[#1a1816] rounded-lg p-3 border border-[#3a352f]">
                            <div className="flex items-center gap-2 mb-1">
                              <Target className="w-4 h-4 text-[#C59D52]" />
                              <span className="text-xs text-gray-400">Objectives</span>
                            </div>
                            <p className="text-xl font-bold text-white">{stats.activeObjectives || 0}</p>
                            <p className="text-xs text-gray-500 mt-0.5">of {stats.objectiveCount || 0} total</p>
                          </div>
                        </div>

                        <div 
                          onClick={() => navigate(`/coach/client/${client.id}`)}
                          className="flex items-center gap-2 text-sm text-gray-400 pt-4 border-t border-[#3a352f] cursor-pointer"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Last session: {formatDate(stats.lastSessionDate)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <BrandingSettings />
          )}

          {/* New Client Modal */}
          {showNewClientModal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-8" onClick={handleCancelNewClient}>
              <div
                className="bg-[#2a2520] rounded-lg p-6 max-w-4xl w-full mx-4 border border-[#3a352f] shadow-xl my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-white">Create New Client</h3>
                  <button
                    onClick={handleCancelNewClient}
                    className="p-1 rounded-lg hover:bg-[#3a352f] text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleClientCreate} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Client Name *</label>
                        <input
                          type="text"
                          value={newClientFormData.name}
                          onChange={(e) => handleNewClientFormChange('name', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                          required
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                        <input
                          type="email"
                          value={newClientFormData.email}
                          onChange={(e) => handleNewClientFormChange('email', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                        <input
                          type="tel"
                          value={newClientFormData.phone}
                          onChange={(e) => handleNewClientFormChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth</label>
                        <input
                          type="date"
                          value={newClientFormData.date_of_birth}
                          onChange={(e) => handleNewClientFormChange('date_of_birth', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                        <select
                          value={newClientFormData.status}
                          onChange={(e) => handleNewClientFormChange('status', e.target.value)}
                          className="w-full bg-[#1a1816] text-white px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Preferred Contact Method</label>
                        <select
                          value={newClientFormData.preferred_contact_method}
                          onChange={(e) => handleNewClientFormChange('preferred_contact_method', e.target.value)}
                          className="w-full bg-[#1a1816] text-white px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        >
                          <option value="">Select...</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="text">Text Message</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Address</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-2">Street Address</label>
                        <input
                          type="text"
                          value={newClientFormData.address_street}
                          onChange={(e) => handleNewClientFormChange('address_street', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                        <input
                          type="text"
                          value={newClientFormData.address_city}
                          onChange={(e) => handleNewClientFormChange('address_city', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">State/Province</label>
                        <input
                          type="text"
                          value={newClientFormData.address_state}
                          onChange={(e) => handleNewClientFormChange('address_state', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">ZIP/Postal Code</label>
                        <input
                          type="text"
                          value={newClientFormData.address_zip}
                          onChange={(e) => handleNewClientFormChange('address_zip', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                        <input
                          type="text"
                          value={newClientFormData.address_country}
                          onChange={(e) => handleNewClientFormChange('address_country', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Professional Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Occupation</label>
                        <input
                          type="text"
                          value={newClientFormData.occupation}
                          onChange={(e) => handleNewClientFormChange('occupation', e.target.value)}
                          placeholder="Job title"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                        <input
                          type="text"
                          value={newClientFormData.company}
                          onChange={(e) => handleNewClientFormChange('company', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
                        <input
                          type="text"
                          value={newClientFormData.timezone}
                          onChange={(e) => handleNewClientFormChange('timezone', e.target.value)}
                          placeholder="e.g., EST, PST, UTC"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Referral Source</label>
                        <input
                          type="text"
                          value={newClientFormData.referral_source}
                          onChange={(e) => handleNewClientFormChange('referral_source', e.target.value)}
                          placeholder="How did they find you?"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Emergency Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Emergency Contact Name</label>
                        <input
                          type="text"
                          value={newClientFormData.emergency_contact_name}
                          onChange={(e) => handleNewClientFormChange('emergency_contact_name', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Emergency Contact Phone</label>
                        <input
                          type="tel"
                          value={newClientFormData.emergency_contact_phone}
                          onChange={(e) => handleNewClientFormChange('emergency_contact_phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Additional Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Goals Summary</label>
                      <textarea
                        value={newClientFormData.goals_summary}
                        onChange={(e) => handleNewClientFormChange('goals_summary', e.target.value)}
                        placeholder="High-level summary of client goals..."
                        rows={3}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Medical Conditions / Allergies</label>
                      <textarea
                        value={newClientFormData.medical_conditions}
                        onChange={(e) => handleNewClientFormChange('medical_conditions', e.target.value)}
                        placeholder="Any relevant medical information..."
                        rows={2}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                      <textarea
                        value={newClientFormData.notes}
                        onChange={(e) => handleNewClientFormChange('notes', e.target.value)}
                        placeholder="Additional notes about the client..."
                        rows={4}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-[#3a352f]">
                    <button
                      type="submit"
                      className="flex-1 bg-[#D4A574] hover:bg-[#C59D52] text-white px-6 py-3 rounded-lg font-semibold transition-all"
                    >
                      Create Client
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelNewClient}
                      className="px-6 py-3 bg-[#1f1d1b] hover:bg-[#3a352f] text-white rounded-lg transition-all border border-[#3a352f] font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Client Modal */}
          {editingClient && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-8" onClick={handleCancelEdit}>
              <div 
                className="bg-[#2a2520] rounded-lg p-6 max-w-4xl w-full mx-4 border border-[#3a352f] shadow-xl my-auto" 
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-white">Edit Client Profile</h3>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 rounded-lg hover:bg-[#3a352f] text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handleClientUpdate} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Client Name *</label>
                        <input
                          type="text"
                          value={editFormData.name}
                          onChange={(e) => handleFormChange('name', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                          required
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => handleFormChange('email', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                        <input
                          type="tel"
                          value={editFormData.phone}
                          onChange={(e) => handleFormChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth</label>
                        <input
                          type="date"
                          value={editFormData.date_of_birth}
                          onChange={(e) => handleFormChange('date_of_birth', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                        <select
                          value={editFormData.status}
                          onChange={(e) => handleFormChange('status', e.target.value)}
                          className="w-full bg-[#1a1816] text-white px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Preferred Contact Method</label>
                        <select
                          value={editFormData.preferred_contact_method}
                          onChange={(e) => handleFormChange('preferred_contact_method', e.target.value)}
                          className="w-full bg-[#1a1816] text-white px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        >
                          <option value="">Select...</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="text">Text Message</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Address</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-2">Street Address</label>
                        <input
                          type="text"
                          value={editFormData.address_street}
                          onChange={(e) => handleFormChange('address_street', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                        <input
                          type="text"
                          value={editFormData.address_city}
                          onChange={(e) => handleFormChange('address_city', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">State/Province</label>
                        <input
                          type="text"
                          value={editFormData.address_state}
                          onChange={(e) => handleFormChange('address_state', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">ZIP/Postal Code</label>
                        <input
                          type="text"
                          value={editFormData.address_zip}
                          onChange={(e) => handleFormChange('address_zip', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                        <input
                          type="text"
                          value={editFormData.address_country}
                          onChange={(e) => handleFormChange('address_country', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Professional Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Occupation</label>
                        <input
                          type="text"
                          value={editFormData.occupation}
                          onChange={(e) => handleFormChange('occupation', e.target.value)}
                          placeholder="Job title"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                        <input
                          type="text"
                          value={editFormData.company}
                          onChange={(e) => handleFormChange('company', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
                        <input
                          type="text"
                          value={editFormData.timezone}
                          onChange={(e) => handleFormChange('timezone', e.target.value)}
                          placeholder="e.g., EST, PST, UTC"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Referral Source</label>
                        <input
                          type="text"
                          value={editFormData.referral_source}
                          onChange={(e) => handleFormChange('referral_source', e.target.value)}
                          placeholder="How did they find you?"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Emergency Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Emergency Contact Name</label>
                        <input
                          type="text"
                          value={editFormData.emergency_contact_name}
                          onChange={(e) => handleFormChange('emergency_contact_name', e.target.value)}
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Emergency Contact Phone</label>
                        <input
                          type="tel"
                          value={editFormData.emergency_contact_phone}
                          onChange={(e) => handleFormChange('emergency_contact_phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-[#C59D52] border-b border-[#3a352f] pb-2">Additional Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Goals Summary</label>
                      <textarea
                        value={editFormData.goals_summary}
                        onChange={(e) => handleFormChange('goals_summary', e.target.value)}
                        placeholder="High-level summary of client goals..."
                        rows={3}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Medical Conditions / Allergies</label>
                      <textarea
                        value={editFormData.medical_conditions}
                        onChange={(e) => handleFormChange('medical_conditions', e.target.value)}
                        placeholder="Any relevant medical information..."
                        rows={2}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                      <textarea
                        value={editFormData.notes}
                        onChange={(e) => handleFormChange('notes', e.target.value)}
                        placeholder="Additional notes about the client..."
                        rows={4}
                        className="w-full bg-[#1a1816] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 focus:border-[#C59D52]/50 font-medium resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-[#3a352f]">
                    <button
                      type="submit"
                      className="flex-1 bg-[#D4A574] hover:bg-[#C59D52] text-white px-6 py-3 rounded-lg font-semibold transition-all"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-6 py-3 bg-[#1f1d1b] hover:bg-[#3a352f] text-white rounded-lg transition-all border border-[#3a352f] font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Invitation Modal */}
          {showInvitationModal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => {
              setShowInvitationModal(false);
              setInvitationLink(null);
              setInvitingClient(null);
            }}>
              <div
                className="bg-[#2a2520] rounded-lg p-6 max-w-md w-full mx-4 border border-[#3a352f] shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">Send Invitation</h3>
                  <button
                    onClick={() => {
                      setShowInvitationModal(false);
                      setInvitationLink(null);
                      setInvitingClient(null);
                    }}
                    className="p-1 rounded-lg hover:bg-[#3a352f] text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-gray-300 mb-6 text-sm">
                  Send an invitation to <span className="font-semibold text-white">{invitingClient?.name}</span> to create their account.
                </p>

                <div className="space-y-3 mb-6">
                  {invitingClient?.email && (
                    <button
                      onClick={handleSendInvitationEmail}
                      className="w-full bg-[#D4A574] hover:bg-[#C59D52] text-white px-4 py-3 rounded-lg transition-all font-semibold flex items-center justify-center gap-2"
                    >
                      <Mail className="w-5 h-5" />
                      <span>Send via Email to {invitingClient.email}</span>
                    </button>
                  )}

                  <button
                    onClick={copyToClipboard}
                    className="w-full bg-[#1a1816] hover:bg-[#2a2520] text-white px-4 py-3 rounded-lg transition-all font-semibold flex items-center justify-center gap-2 border border-[#3a352f]"
                  >
                    {copySuccess ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Link Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" />
                        <span>Copy Invitation Link</span>
                      </>
                    )}
                  </button>
                </div>

                {!invitingClient?.email && (
                  <p className="text-yellow-400 text-xs mb-4 flex items-center gap-2">
                    <span className="text-lg">⚠️</span>
                    <span>No email on file. You'll need to share the link manually.</span>
                  </p>
                )}

                <div className="bg-[#1a1816] border border-[#3a352f] rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-400 mb-2">Invitation Link:</p>
                  <p className="break-all text-xs text-gray-300">{invitationLink}</p>
                </div>

                <button
                  onClick={() => {
                    setShowInvitationModal(false);
                    setInvitationLink(null);
                    setInvitingClient(null);
                  }}
                  className="w-full px-6 py-2.5 bg-[#1f1d1b] hover:bg-[#3a352f] text-white rounded-lg transition-all border border-[#3a352f] font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoachCRM;

